import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useLang, tr } from "../contexts/LangContext";
import api from "../lib/api";
import FleetManager from "../components/FleetManager";
import {
  Ship, Warehouse, UserCog, Plus, Trash2, Calendar, X, Building2, Anchor, MapPin, Save,
} from "lucide-react";

const TABS = ["boats", "infrastructure", "port", "staff"];
const TAB_LABEL = {
  boats: "Bateaux & planning", infrastructure: "Infrastructure",
  port: "Port — garages & parkings", staff: "Personnel & bénévoles",
};
const TAB_ICON = { boats: Ship, infrastructure: Warehouse, port: MapPin, staff: UserCog };

export default function Gestion() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [tab, setTab] = useState("boats");

  if (!user || !["admin", "manager"].includes(user.role)) return null;

  return (
    <div className="pt-24 md:pt-28 pb-16 md:pb-24 min-h-screen bg-sand" data-testid="gestion-page">
      <div className="max-w-7xl mx-auto px-4 md:px-10 lg:px-12">
        <div className="flex items-center gap-3 mb-3">
          <Building2 className="w-5 h-5 text-navy" strokeWidth={1.5} />
          <div className="label-eyebrow text-club-red">{tr("Espace Gestion")}</div>
        </div>
        <h1 className="font-serif text-4xl text-navy">{tr("Opérations du club")}</h1>
        <div className="divider-gold mt-5 mb-8" />

        <div className="flex flex-nowrap gap-1 border-b border-slate-200 mb-8 overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 scrollbar-thin" data-testid="gestion-tabs">
          {TABS.map((k) => {
            const Icon = TAB_ICON[k];
            return (
              <button
                key={k} onClick={() => setTab(k)}
                className={`px-4 py-3 text-xs uppercase tracking-[0.2em] border-b-2 flex items-center gap-2 ${
                  tab === k ? "border-club-red text-club-red font-semibold" : "border-transparent text-slate-500 hover:text-navy"
                }`}
                data-testid={`gestion-tab-${k}`}
              >
                <Icon className="w-4 h-4" /> {tr(TAB_LABEL[k])}
              </button>
            );
          })}
        </div>

        {tab === "boats" && <BoatsTab lang={lang} />}
        {tab === "infrastructure" && <InfrastructureTab />}
        {tab === "port" && <PortMapTab />}
        {tab === "staff" && <StaffTab />}
      </div>
    </div>
  );
}

/* ===================== BOATS + RESERVATIONS ===================== */
function BoatsTab({ lang }) {
  const [boats, setBoats] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [members, setMembers] = useState([]);
  const [showRes, setShowRes] = useState(false);
  const [resForm, setResForm] = useState({ boat_id: "", member_id: "", date: "", time_slot: "morning", notes: "" });
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const [b, r, m] = await Promise.all([
        api.get("/gestion/boats"),
        api.get("/gestion/boat-reservations"),
        api.get("/gestion/members"),
      ]);
      setBoats(b.data); setReservations(r.data); setMembers(m.data);
    } catch { /* ignore */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const submitRes = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/gestion/boat-reservations", resForm);
      setShowRes(false);
      setResForm({ boat_id: "", member_id: "", date: "", time_slot: "morning", notes: "" });
      load();
    } catch (e) { setErr(e.response?.data?.detail || tr("Conflit ou erreur")); }
  };

  const delRes = async (id) => {
    if (!window.confirm(tr("Annuler cette réservation ?"))) return;
    await api.delete(`/gestion/boat-reservations/${id}`);
    load();
  };

  return (
    <div data-testid="gestion-boats">
      <FleetManager onFleetChanged={load} />

      <div className="flex flex-wrap gap-3 mb-6 mt-10">
        <button onClick={() => setShowRes(!showRes)} className="inline-flex items-center gap-2 bg-club-red text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="res-toggle">
          <Calendar className="w-4 h-4" />{tr("Réserver un créneau")}</button>
      </div>
      {err && <div className="text-sm text-red-600 mb-4" data-testid="boats-error">{err}</div>}

      {showRes && (
        <form onSubmit={submitRes} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="res-form">
          <select required value={resForm.boat_id} onChange={(e) => setResForm({ ...resForm, boat_id: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="res-boat">
            <option value="">{tr("Sélectionner un bateau")}</option>
            {boats.filter((b) => b.is_active !== false).map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          <select required value={resForm.member_id} onChange={(e) => setResForm({ ...resForm, member_id: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="res-member">
            <option value="">{tr("Sélectionner un membre")}</option>
            {members.map((m) => <option key={m.id} value={m.id}>{m.name} — {m.email}</option>)}
          </select>
          <input required type="date" value={resForm.date} onChange={(e) => setResForm({ ...resForm, date: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="res-date" />
          <select required value={resForm.time_slot} onChange={(e) => setResForm({ ...resForm, time_slot: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="res-slot">
            <option value="morning">{tr("Matin (9h-13h)")}</option>
            <option value="afternoon">{tr("Après-midi (14h-18h)")}</option>
            <option value="fullday">{tr("Journée complète")}</option>
          </select>
          <input placeholder="Notes" value={resForm.notes} onChange={(e) => setResForm({ ...resForm, notes: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="res-notes" />
          <button type="submit" className="md:col-span-2 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="res-submit">Confirmer</button>
        </form>
      )}

      <h3 className="font-serif text-2xl text-navy mb-3">{tr("Planning des réservations")} ({reservations.length})</h3>
      <div className="divider-gold mb-4" />
      {reservations.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-8 text-center text-slate-500">{tr("Aucune réservation.")}</div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy text-white text-left">
              <tr>
                {["Date", "Bateau", "Créneau", "Membre", ""].map((h) => (
                  <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {reservations.map((r) => (
                <tr key={r.id} className="border-t border-slate-100" data-testid={`res-row-${r.id}`}>
                  <td className="px-4 py-3">{new Date(r.date).toLocaleDateString(lang === "en" ? "en-GB" : lang)}</td>
                  <td className="px-4 py-3 text-navy font-medium">{r.boat?.name || "—"}</td>
                  <td className="px-4 py-3 text-xs uppercase tracking-widest">{r.time_slot}</td>
                  <td className="px-4 py-3 text-slate-600">{r.member?.name || "—"}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => delRes(r.id)} className="text-red-600 hover:text-red-800" data-testid={`res-del-${r.id}`}><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ===================== INFRASTRUCTURE ===================== */
function InfrastructureTab() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ type: "ponton", name: "", capacity: 0, occupied: 0, notes: "" });

  const load = async () => {
    try { const { data } = await api.get("/gestion/infrastructure"); setItems(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/gestion/infrastructure", {
      ...form, capacity: parseInt(form.capacity) || 0, occupied: parseInt(form.occupied) || 0,
    });
    setShow(false);
    setForm({ type: "ponton", name: "", capacity: 0, occupied: 0, notes: "" });
    load();
  };

  const updateOccupied = async (id, occupied) => {
    const fd = new FormData();
    fd.append("occupied", occupied);
    await api.patch(`/gestion/infrastructure/${id}`, fd);
    load();
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer cet élément ?"))) return;
    await api.delete(`/gestion/infrastructure/${id}`);
    load();
  };

  return (
    <div data-testid="gestion-infrastructure">
      <button onClick={() => setShow(!show)} className="mb-6 inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="infra-toggle">
        <Plus className="w-4 h-4" />{tr("Ajouter")}</button>

      {show && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="infra-form">
          <select required value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="infra-type">
            <option value="ponton">Ponton</option>
            <option value="hangar">Hangar</option>
            <option value="parking_zone">Zone parking</option>
            <option value="crane">Grue</option>
            <option value="other">{tr("Autre")}</option>
          </select>
          <input required placeholder={tr("Nom")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="infra-name" />
          <input type="number" placeholder={tr("Capacité")} value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="infra-capacity" />
          <input type="number" placeholder={tr("Occupé")} value={form.occupied} onChange={(e) => setForm({ ...form, occupied: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="infra-occupied" />
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-slate-300 px-3 py-2 md:col-span-2" data-testid="infra-notes" />
          <button type="submit" className="md:col-span-2 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="infra-submit">{tr("Enregistrer")}</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((it) => {
          const pct = it.capacity ? Math.min(100, Math.round((it.occupied / it.capacity) * 100)) : 0;
          return (
            <div key={it.id} className="bg-white border border-slate-200 p-5" data-testid={`infra-${it.id}`}>
              <Warehouse className="w-6 h-6 text-club-red mb-3" strokeWidth={1.5} />
              <div className="font-serif text-xl text-navy">{it.name}</div>
              <div className="text-xs uppercase tracking-widest text-club-yellow-dark mt-1">{it.type}</div>
              <div className="mt-3">
                <div className="flex justify-between text-xs text-slate-500 mb-1">
                  <span>{tr("Occupé")}</span><span>{it.occupied} / {it.capacity}</span>
                </div>
                <div className="h-2 bg-slate-100 overflow-hidden">
                  <div className={`h-full ${pct >= 90 ? "bg-club-red" : pct >= 60 ? "bg-club-yellow" : "bg-green-600"}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
              {it.notes && <div className="text-xs text-slate-500 mt-3 italic">{it.notes}</div>}
              <div className="flex gap-2 mt-4">
                <input type="number" defaultValue={it.occupied} className="w-20 border border-slate-300 px-2 py-1 text-sm" data-testid={`infra-occ-${it.id}`} onBlur={(e) => updateOccupied(it.id, e.target.value)} />
                <button onClick={() => del(it.id)} className="ml-auto text-[10px] uppercase tracking-widest text-red-600 hover:underline" data-testid={`infra-del-${it.id}`}>{tr("Supprimer")}</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ===================== STAFF / VOLUNTEERS ===================== */
function StaffTab() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", role: "moniteur", email: "", phone: "", skills: "", notes: "" });

  const load = async () => {
    try { const { data } = await api.get("/gestion/staff"); setItems(data); } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    await api.post("/gestion/staff", form);
    setShow(false);
    setForm({ name: "", role: "moniteur", email: "", phone: "", skills: "", notes: "" });
    load();
  };

  const del = async (id) => {
    if (!window.confirm(tr("Supprimer cette personne ?"))) return;
    await api.delete(`/gestion/staff/${id}`);
    load();
  };

  const roleBadge = {
    moniteur: "bg-club-red text-white",
    securite: "bg-navy text-white",
    benevole: "bg-club-yellow text-navy",
    comite: "bg-slate-700 text-white",
    autre: "bg-slate-200 text-slate-700",
  };

  return (
    <div data-testid="gestion-staff">
      <button onClick={() => setShow(!show)} className="mb-6 inline-flex items-center gap-2 bg-navy text-white px-5 py-2.5 text-xs uppercase tracking-[0.2em] hover:bg-club-red" data-testid="staff-toggle">
        <Plus className="w-4 h-4" />{tr("Ajouter")}</button>

      {show && (
        <form onSubmit={submit} className="bg-white border border-slate-200 p-6 mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="staff-form">
          <input required placeholder={tr("Nom")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="staff-name" />
          <select required value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="staff-role">
            <option value="moniteur">Moniteur</option>
            <option value="securite">{tr("Sécurité")}</option>
            <option value="benevole">{tr("Bénévole")}</option>
            <option value="comite">{tr("Comité")}</option>
            <option value="autre">{tr("Autre")}</option>
          </select>
          <input type="email" placeholder={tr("Email")} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="staff-email" />
          <input placeholder={tr("Téléphone")} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="staff-phone" />
          <input placeholder={tr("Compétences")} value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="staff-skills" />
          <input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="border border-slate-300 px-3 py-2" data-testid="staff-notes" />
          <button type="submit" className="md:col-span-2 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark" data-testid="staff-submit">{tr("Enregistrer")}</button>
        </form>
      )}

      <div className="bg-white border border-slate-200 overflow-x-auto" data-testid="staff-table">
        <table className="w-full text-sm">
          <thead className="bg-navy text-white text-left">
            <tr>{["Nom", "Rôle", "Email", "Téléphone", "Compétences", ""].map((h) => (
              <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest font-medium">{tr(h)}</th>
            ))}</tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-slate-100" data-testid={`staff-row-${s.id}`}>
                <td className="px-4 py-3 text-navy font-medium">{s.name}</td>
                <td className="px-4 py-3"><span className={`text-[10px] uppercase tracking-widest px-2 py-1 font-semibold ${roleBadge[s.role] || ""}`}>{s.role}</span></td>
                <td className="px-4 py-3 text-slate-600">{s.email || "—"}</td>
                <td className="px-4 py-3 text-slate-600">{s.phone || "—"}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{s.skills || "—"}</td>
                <td className="px-4 py-3 text-right"><button onClick={() => del(s.id)} className="text-red-600 hover:text-red-800" data-testid={`staff-del-${s.id}`}><Trash2 className="w-4 h-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =========================================================== */
/*  PORT MAP TAB — Garages (A/B/C béton, D bois) + Parkings    */
/* =========================================================== */
function PortMapTab() {
  const [slots, setSlots] = useState([]);
  const [parkings, setParkings] = useState([]);
  const [editing, setEditing] = useState(null); // slot being assigned
  const [members, setMembers] = useState([]);

  const load = async () => {
    try {
      const [{ data: s }, { data: pubLayout }, { data: mems }] = await Promise.all([
        api.get("/port/slots"),
        api.get("/port/layout"),
        api.get("/admin/users").catch(() => api.get("/treasury/members")).catch(() => ({ data: [] })),
      ]);
      setSlots(s || []);
      setParkings(pubLayout?.parkings || []);
      setMembers(mems || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const byZone = (zone, kind) => slots.filter((s) => s.zone === zone && s.kind === kind);
  const stats = {
    total: slots.length,
    occupied: slots.filter((s) => s.status === "occupied").length,
    parkings: parkings.reduce((a, b) => a + (b.capacity || 0), 0),
  };

  return (
    <div data-testid="port-map-tab">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h2 className="font-serif text-2xl text-navy">{tr("Plan du port")}</h2>
          <p className="text-sm text-slate-500 mt-1">{tr("Cliquez sur une cellule pour assigner un membre, ajuster les places ou libérer.")}</p>
        </div>
        <div className="flex gap-2">
          <KPISmall label={tr("Garages")} value={`${stats.occupied} / ${stats.total}`} hint={tr("occupés")} color="bg-navy" />
          <KPISmall label={tr("Parkings")} value={stats.parkings} hint="places" color="bg-club-red" />
        </div>
      </div>

      <Legend />

      {/* RYCM Schema layout — mirrors the hand-drawn plan */}
      <div className="bg-sand border border-slate-200 p-4 md:p-6 mt-6 overflow-x-auto">
        <div className="min-w-[820px] grid grid-cols-12 gap-3 text-[10px]">
          {/* Top row — A01..A16 horizontal block */}
          <div className="col-span-7">
            <ZoneTitle label={tr("GARAGE BÉTON · Zone A (16)")} />
            <div className="grid gap-0.5" style={{ gridTemplateColumns: "repeat(16, minmax(0, 1fr))" }}>
              {byZone("A", "beton").map((s) => <SlotCell key={s.id} slot={s} onClick={() => setEditing(s)} />)}
            </div>
          </div>
          <div className="col-span-5 flex items-center text-xs text-slate-400 italic justify-end">
            ← Hangar / R.C.A.E.
          </div>

          {/* Mid-left block — Parking L · J · K (parking zones) */}
          <div className="col-span-3">
            <ZoneTitle label={tr("PARKINGS")} />
            <div className="grid grid-cols-1 gap-1.5">
              {["L", "J", "K"].map((z) => (
                <ParkingCell key={z} zone={z} parkings={parkings} onReload={load} />
              ))}
            </div>
          </div>

          {/* Mid-center — B17..B28 vertical */}
          <div className="col-span-3">
            <ZoneTitle label={tr("ZONE B (12)")} />
            <div className="grid grid-cols-2 gap-0.5">
              {byZone("B", "beton").map((s) => <SlotCell key={s.id} slot={s} onClick={() => setEditing(s)} />)}
            </div>
          </div>

          {/* Mid-right — D40..D53 in 2 columns */}
          <div className="col-span-3">
            <ZoneTitle label={tr("GARAGE BOIS · Zone D (14)")} />
            <div className="grid grid-cols-2 gap-0.5">
              {byZone("D", "bois").map((s) => <SlotCell key={s.id} slot={s} onClick={() => setEditing(s)} />)}
            </div>
          </div>

          {/* Right column — Parkings A · B · C · D + Club House */}
          <div className="col-span-3">
            <ZoneTitle label={tr("PARKINGS CÔTÉ QUAI")} />
            <div className="grid grid-cols-1 gap-1.5">
              {["A", "B", "C", "D"].map((z) => (
                <ParkingCell key={z} zone={z} parkings={parkings} onReload={load} />
              ))}
            </div>
          </div>

          {/* Bottom — C29..C33 horizontal */}
          <div className="col-span-12 mt-3">
            <ZoneTitle label={tr("ZONE C (5)")} />
            <div className="grid grid-cols-5 gap-0.5 max-w-[280px]">
              {byZone("C", "beton").map((s) => <SlotCell key={s.id} slot={s} onClick={() => setEditing(s)} />)}
            </div>
          </div>

          <div className="col-span-12 text-center mt-2 italic text-slate-400 text-xs">
            ↓ La Meuse ↓
          </div>
        </div>
      </div>

      {/* Detailed list view (toggle) */}
      <details className="mt-8 bg-white border border-slate-200">
        <summary className="px-4 py-3 cursor-pointer font-serif text-navy">{tr("Vue détaillée — Liste des garages")}</summary>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-navy text-white text-left">
              <tr>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">{tr("Code")}</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">{tr("Type")}</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">{tr("Statut")}</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">{tr("Membre")}</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">{tr("Bateau")}</th>
                <th className="px-3 py-2 text-xs uppercase tracking-widest font-medium">Loyer</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {slots.map((s) => (
                <tr key={s.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`slot-row-${s.code}`}>
                  <td className="px-3 py-2 font-mono text-navy">{s.code}</td>
                  <td className="px-3 py-2 capitalize text-slate-600">{s.kind}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${s.status === "occupied" ? "bg-red-100 text-red-800" : "bg-emerald-100 text-emerald-800"}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-slate-700">{s.occupant_name || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{s.boat_name || "—"}</td>
                  <td className="px-3 py-2 text-slate-500">{s.annual_fee ? `€ ${s.annual_fee.toFixed(2)}` : "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => setEditing(s)} className="text-[10px] uppercase tracking-widest text-navy hover:underline" data-testid={`slot-edit-${s.code}`}>{tr("Modifier")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </details>

      {editing && <SlotEditModal slot={editing} members={members} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
}

function KPISmall({ label, value, hint, color }) {
  return (
    <div className={`${color} text-white px-4 py-2.5 min-w-[120px]`}>
      <div className="text-[10px] uppercase tracking-[0.2em] opacity-80">{label}</div>
      <div className="font-serif text-xl leading-tight">{value}</div>
      {hint && <div className="text-[10px] opacity-70">{hint}</div>}
    </div>
  );
}

function ZoneTitle({ label }) {
  return <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1.5 font-semibold">{label}</div>;
}

function Legend() {
  return (
    <div className="flex flex-wrap gap-3 mt-4 text-xs">
      <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-emerald-200 border border-emerald-400"></span>{tr("disponible")}</span>
      <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-rose-300 border border-rose-500"></span>{tr("occupé")}</span>
      <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-amber-100 border border-amber-400"></span>{tr("garage béton")}</span>
      <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-orange-100 border border-orange-400"></span>{tr("garage bois")}</span>
      <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 bg-cyan-100 border border-cyan-400"></span> parking</span>
    </div>
  );
}

function SlotCell({ slot, onClick }) {
  const occupied = slot.status === "occupied";
  const kindBorder = slot.kind === "bois" ? "border-orange-400" : "border-amber-400";
  return (
    <button
      onClick={onClick}
      title={`${slot.code} · ${slot.occupant_name || "Disponible"}`}
      className={`relative border ${kindBorder} ${occupied ? "bg-rose-300 hover:bg-rose-400" : "bg-emerald-100 hover:bg-emerald-200"} px-1 py-1 text-[9px] font-mono text-navy transition-colors min-w-[34px]`}
      data-testid={`slot-${slot.code}`}
    >
      {slot.code}
    </button>
  );
}

function ParkingCell({ zone, parkings, onReload }) {
  const p = parkings.find((x) => x.zone === zone) || { zone, capacity: 0 };
  const [edit, setEdit] = useState(false);
  const [val, setVal] = useState(p.capacity);
  const save = async () => {
    try {
      await api.patch(`/port/parking-zones/${zone}`, { capacity: parseInt(val) || 0 });
      setEdit(false); onReload();
    } catch { /* ignore */ }
  };
  return (
    <div className="bg-cyan-50 border border-cyan-400 p-2 text-[10px] flex items-center justify-between gap-2" data-testid={`parking-${zone}`}>
      <div>
        <div className="font-mono text-navy font-semibold">Parking {zone}</div>
        {edit ? (
          <div className="flex items-center gap-1 mt-1">
            <input type="number" min="0" max="500" value={val} onChange={(e) => setVal(e.target.value)} className="w-14 border border-slate-300 px-1 py-0.5 text-[10px]" data-testid={`parking-${zone}-input`} />
            <button onClick={save} className="text-emerald-700" data-testid={`parking-${zone}-save`}><Save className="w-3 h-3" /></button>
            <button onClick={() => { setEdit(false); setVal(p.capacity); }} className="text-slate-400"><X className="w-3 h-3" /></button>
          </div>
        ) : (
          <div className="text-slate-600">{p.capacity} place{p.capacity !== 1 ? "s" : ""}</div>
        )}
      </div>
      {!edit && (
        <button onClick={() => setEdit(true)} className="text-cyan-700 hover:underline text-[10px] uppercase tracking-widest" data-testid={`parking-${zone}-edit`}>{tr("Éditer")}</button>
      )}
    </div>
  );
}

function SlotEditModal({ slot, members, onClose, onSaved }) {
  const [form, setForm] = useState({
    member_id: slot.member_id || "",
    occupant_name: slot.occupant_name || "",
    boat_name: slot.boat_name || "",
    annual_fee: slot.annual_fee || "",
    notes: slot.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...form };
      if (payload.annual_fee === "") delete payload.annual_fee;
      else payload.annual_fee = parseFloat(payload.annual_fee);
      await api.patch(`/port/slots/${slot.id}`, payload);
      onSaved(); onClose();
    } catch (e) { setErr(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  const release = async () => {
    if (!window.confirm(`Libérer le garage ${slot.code} ?`)) return;
    setBusy(true);
    try {
      await api.post(`/port/slots/${slot.id}/release`);
      onSaved(); onClose();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-navy/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="slot-edit-modal">
      <div className="bg-white max-w-lg w-full p-6 md:p-8 relative my-12" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-500 hover:text-navy"><X className="w-5 h-5" /></button>
        <div className="label-eyebrow text-club-red mb-2">Garage {slot.kind === "bois" ? "bois" : "béton"} · {slot.zone}</div>
        <h2 className="font-serif text-3xl text-navy font-mono">{slot.code}</h2>
        <div className="divider-gold mt-3 mb-5" />

        <div className="space-y-3">
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{tr("Membre attribué (optionnel)")}</label>
            <select
              value={form.member_id}
              onChange={(e) => {
                const id = e.target.value;
                const m = members.find((x) => x.id === id);
                setForm({ ...form, member_id: id, occupant_name: m ? (m.name || m.email) : form.occupant_name });
              }}
              className="w-full border border-slate-300 px-3 py-2 text-sm"
              data-testid="slot-member-select"
            >
              <option value="">— Aucun (saisie libre ci-dessous) —</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name || `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{tr("Occupant (nom affiché)")}</label>
            <input value={form.occupant_name} onChange={(e) => setForm({ ...form, occupant_name: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm" data-testid="slot-occupant" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">{tr("Bateau")}</label>
            <input value={form.boat_name} onChange={(e) => setForm({ ...form, boat_name: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm" data-testid="slot-boat" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Loyer annuel (€)</label>
            <input type="number" step="0.01" value={form.annual_fee} onChange={(e) => setForm({ ...form, annual_fee: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm" data-testid="slot-fee" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-slate-500 block mb-1.5">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border border-slate-300 px-3 py-2 text-sm" data-testid="slot-notes" />
          </div>
        </div>

        {err && <div className="text-sm text-red-600 mt-3">{err}</div>}

        <div className="flex gap-2 mt-6">
          {slot.status === "occupied" && (
            <button onClick={release} disabled={busy} className="border border-red-500 text-red-600 px-4 py-3 text-xs uppercase tracking-[0.2em] hover:bg-red-50 disabled:opacity-60" data-testid="slot-release">{tr("Libérer")}</button>
          )}
          <button onClick={save} disabled={busy} className="flex-1 bg-club-red text-white py-3 text-xs uppercase tracking-[0.2em] hover:bg-club-red-dark disabled:opacity-60" data-testid="slot-save">
            {busy ? "…" : tr("Enregistrer")}
          </button>
        </div>
      </div>
    </div>
  );
}

